import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import T from 'i18n';
import { useDispatch, useSelector } from 'react-redux';
import { useFieldArray, useForm } from 'react-hook-form';
import { Button, Checkbox, CircularProgress, FormControlLabel, IconButton } from '@mui/material';
import { AddCard, CheckCircleOutline, Delete, ErrorOutline, WarningAmber } from '@mui/icons-material';
import Split from 'react-split';
import { Col, Form, Row } from 'react-bootstrap';
import { TextForm } from 'features/common';
import { Modal } from 'features/common/MaterialBasedComponents';
import { selectCurrentCaseId } from 'common/selectors';
import { useToasts } from 'react-toast-notifications';
import history from 'common/history';
import FileViewer from '../../FileViewer';
import { useFetchFileDetails } from '../../redux/fetchFileDetails';
import { useFetchNotifications } from '../../redux/fetchNotifications';
import { getScrollContainer } from 'features/viewing/AcusoftViewer/AcusoftViewerControl/utils';
import MoveDocument from './MoveDocument';
import * as actions from '../../redux/actions';
import { generateRemovePagesFolderName } from '../utils';

const CHECKBOX_CONTAINER_CLASS = 'remove-pages-checkbox-container';
const CHECKBOX_INPUT_CLASS = 'remove-pages-checkbox-input';
const CHECKBOX_OVERLAY_CLASS = 'remove-pages-checkbox-overlay';
const CHECKBOX_SPINNER_CLASS = 'remove-pages-checkbox-spinner';
const PAGE_BATCH_SIZE = 100;

const ModalRemovePages = ({ show, handleClose, selectedRows }) => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const selectedFile = selectedRows?.[selectedRows.length - 1];
  const selectedFileId = selectedFile?.fileId || selectedFile?.id;
  const selectedFolderFileId = selectedFile?.folderFileId;
  const currentCaseId = useSelector(selectCurrentCaseId);
  const { fetchFileDetails } = useFetchFileDetails();
  const { notifications, transientNotifications } = useFetchNotifications();
  const fileViewerRef = useRef(null);
  const viewerWrapperRef = useRef(null);
  const checkboxObserverRef = useRef(null);
  const checkboxHandlerRef = useRef(() => {});
  const overlayContainerRef = useRef(null);
  const overlayCheckboxesRef = useRef(new Map());
  const overlayUpdateRafRef = useRef(null);
  const refreshOverlayPositionsRef = useRef(null);
  const overlayPageAttributesRef = useRef(new Map());
  const overlayBatchLoadingRef = useRef(null);
  const overlayComputedBatchesRef = useRef(new Set());
  const overlayBatchInFlightRef = useRef(null);
  const pendingRemoveRequestIdRef = useRef(0);
  const isMountedRef = useRef(false);
  const isModalOpenRef = useRef(false);
  const removeRequestTrackingRef = useRef({
    active: false,
    startedAtMs: 0,
    sourceFileId: null,
  });
  const isMapDraggingRef = useRef(false);
  const mapDragStartPageRef = useRef(null);
  const mapDragCurrentPageRef = useRef(null);
  const mapDragShouldSelectRef = useRef(null);
  const pageMapContainerRef = useRef(null);
  const pageMapAutoScrollRafRef = useRef(null);
  const pageMapPointerPositionRef = useRef({ x: null, y: null });
  const previewViewerControlRef = useRef(null);
  const numericSelectionsRef = useRef(new Set());
  const transientNotificationLogIndexRef = useRef(0);
  const notificationLogIndexRef = useRef(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshingFile, setIsRefreshingFile] = useState(false);
  const [viewerReady, setViewerReady] = useState(false);
  const [useViewerOverlay, setUseViewerOverlay] = useState(false);
  const [currentPreviewPage, setCurrentPreviewPage] = useState(1);
  const [viewerPageCount, setViewerPageCount] = useState(null);
  const [debugCheckboxStatus, setDebugCheckboxStatus] = useState('');
  const [file, setFile] = useState(selectedFile || null);
  const [viewerInstanceKey, setViewerInstanceKey] = useState(0);
  const [removeRequestStatus, setRemoveRequestStatus] = useState('idle');
  const [removeRequestMessage, setRemoveRequestMessage] = useState('');
  const [removeResultLink, setRemoveResultLink] = useState('');

  const isAsyncStatusView = removeRequestStatus !== 'idle';
  const getSuggestedNewDocName = useCallback((primaryFile, fallbackFile) => {
    const baseName = [
      primaryFile?.folderSpecificName,
      primaryFile?.name,
      primaryFile?.originalName,
      fallbackFile?.folderSpecificName,
      fallbackFile?.name,
      fallbackFile?.originalName,
    ].find((value) => typeof value === 'string' && value.trim());

    return baseName ? `${baseName.trim()} - copy` : '';
  }, []);
  const suggestedNewDocName = useMemo(
    () => getSuggestedNewDocName(selectedFile, file),
    [file, getSuggestedNewDocName, selectedFile],
  );
  const autoGeneratedFolderName = useMemo(
    () => generateRemovePagesFolderName(file),
    [file?.id, file?.name, show],
  );

  const metadataMaxPages = useMemo(() => {
    const lastPageValue = Number(file?.lastPage);
    const startPageValue = Number(file?.startPage);
    if (
      Number.isFinite(lastPageValue) &&
      Number.isFinite(startPageValue) &&
      lastPageValue >= startPageValue
    ) {
      return lastPageValue - startPageValue + 1;
    }
    const pageCountValue = Number(file?.pageCount);
    if (Number.isFinite(pageCountValue) && pageCountValue > 0) return pageCountValue;
    return null;
  }, [file]);

  const maxPages = useMemo(() => {
    if (Number.isFinite(viewerPageCount) && viewerPageCount > 0) {
      return viewerPageCount;
    }
    return metadataMaxPages;
  }, [metadataMaxPages, viewerPageCount]);

  const normalizeCaseLink = useCallback((rawLink) => {
    if (!rawLink) return null;
    let normalized = String(rawLink).trim();
    if (!normalized) return null;

    if (/^https?:\/\//i.test(normalized)) {
      try {
        const parsed = new URL(normalized);
        normalized = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      } catch (error) {
        return null;
      }
    }

    if (!normalized.startsWith('/')) {
      normalized = `/${normalized}`;
    }

    if (!normalized.startsWith('/case')) {
      normalized = `/case${normalized}`;
    }

    return normalized;
  }, []);

  const extractDocumentLink = useCallback(
    (source) => {
      if (!source) return null;
      const directLinkCandidates = [
        source?.link,
        source?.data?.link,
        source?.documentLink,
        source?.data?.documentLink,
        source?.fileLink,
        source?.data?.fileLink,
        source?.notification?.link,
        source?.result?.link,
      ];

      for (const candidate of directLinkCandidates) {
        const normalizedLink = normalizeCaseLink(candidate);
        if (normalizedLink) return normalizedLink;
      }

      return null;
    },
    [normalizeCaseLink],
  );

  const getNotificationTimestampMs = useCallback((notification) => {
    const timestamp =
      notification?.data?.timestampUtc ||
      notification?.createdBy?.date ||
      notification?.timestampUtc ||
      notification?.date;
    if (!timestamp) return 0;
    const parsed = Date.parse(timestamp);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, []);

  const isRemovePagesFailure = useCallback((source) => {
    if (!source) return false;
    if (source?.error || source?.success === false) return true;

    const statusCandidates = [source?.status, source?.data?.status, source?.result?.status];
    const hasFailureStatus = statusCandidates.some((value) => {
      const text = String(value || '').toLowerCase();
      return text.includes('fail') || text.includes('error');
    });
    if (hasFailureStatus) return true;

    const textCandidates = [
      source?.title,
      source?.message,
      source?.body,
      source?.errorMessage,
      source?.data?.title,
      source?.data?.message,
      source?.data?.errorMessage,
    ];
    const fullText = textCandidates
      .filter((value) => value !== undefined && value !== null)
      .map((value) => String(value).toLowerCase())
      .join(' ');

    if (!fullText) return false;
    if (fullText.includes('page removal failed')) return true;
    if (fullText.includes('failed page removal')) return true;
    if (fullText.includes('failed to remove document')) return true;
    if (fullText.includes('remove') && (fullText.includes('fail') || fullText.includes('error'))) {
      return true;
    }

    return false;
  }, []);

  const getRemovePagesStatusMessage = useCallback(
    (source, { isFailure, fallbackMessage }) => {
      const failureOrder = [
        source?.title,
        source?.message,
        source?.body,
        source?.errorMessage,
        source?.data?.title,
        source?.data?.message,
        source?.data?.errorMessage,
      ];
      const successOrder = [
        source?.message,
        source?.title,
        source?.data?.message,
        source?.data?.title,
      ];
      const candidates = isFailure ? failureOrder : successOrder;
      const message = candidates.find(
        (value) => typeof value === 'string' && value.trim().length > 0,
      );
      if (message) return message.trim();
      return fallbackMessage;
    },
    [],
  );

  const findLatestRemoveNotification = useCallback(
    (notificationList) => {
      const tracking = removeRequestTrackingRef.current;
      if (!tracking.active) return null;

      const sourceFileId = tracking.sourceFileId ? String(tracking.sourceFileId) : null;
      const startedAtMs = tracking.startedAtMs || 0;
      const list = Array.isArray(notificationList) ? notificationList : [];
      const candidates = list.filter((notification) => {
        const relatedIds = [
          notification?.entityId,
          notification?.additionalId,
          notification?.data?.fileId,
          notification?.data?.sourceFileId,
          notification?.data?.originalFileId,
          notification?.data?.removedFromFileId,
        ]
          .filter((value) => value !== undefined && value !== null)
          .map((value) => String(value));
        const matchesSourceFile = sourceFileId ? relatedIds.includes(sourceFileId) : false;

        const typeText = String(notification?.type || '').toLowerCase();
        const titleText = String(notification?.title || '').toLowerCase();
        const messageText = String(notification?.message || '').toLowerCase();
        const eventTypeText = String(notification?.data?.eventType || '').toLowerCase();
        const combinedText = `${typeText} ${titleText} ${messageText} ${eventTypeText}`;
        const hasPageToken = combinedText.includes('page');
        const hasRemoveToken =
          combinedText.includes('remove') || combinedText.includes('removal');
        const looksLikeRemoveEvent = hasPageToken && hasRemoveToken;

        if (!looksLikeRemoveEvent && !matchesSourceFile) return false;

        const timestampMs = getNotificationTimestampMs(notification);
        if (startedAtMs && !timestampMs && !matchesSourceFile) {
          return false;
        }
        if (startedAtMs && timestampMs && timestampMs + 1000 < startedAtMs) {
          return false;
        }

        return looksLikeRemoveEvent || matchesSourceFile;
      });

      if (!candidates.length) return null;

      return candidates.reduce((latest, current) =>
        getNotificationTimestampMs(current) >= getNotificationTimestampMs(latest)
          ? current
          : latest,
      );
    },
    [getNotificationTimestampMs],
  );

  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      pagesToRemove: [{ value: '' }],
      createNewDocument: false,
      newDocName: suggestedNewDocName,
      folder: 'auto-generated',
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'pagesToRemove',
  });

  const formValues = watch();
  const createNewDocument = !!formValues?.createNewDocument;
  const newDocNameValue = (formValues?.newDocName || '').trim();
  const newDocFolderId = formValues?.folder;
  const hasNewDocNameError = createNewDocument && !newDocNameValue;
  const hasNewDocFolderError = createNewDocument && !newDocFolderId;
  const hasNewDocConfigError = hasNewDocNameError || hasNewDocFolderError;

  useEffect(() => {
    if (!show) return;
    setFile(selectedFile || null);
    setViewerPageCount(null);
    setValue('newDocName', getSuggestedNewDocName(selectedFile, null), {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [getSuggestedNewDocName, selectedFile, setValue, show]);

  const parsePageToken = useCallback(
    (rawValue) => {
      const value = (rawValue || '').trim();
      if (!value) return { normalized: null, error: null };

      const singleMatch = value.match(/^(\d+)$/);
      if (singleMatch) {
        const num = parseInt(singleMatch[1], 10);
        if (num < 1) return { normalized: null, error: T.translate('case.lessThanOnePageNumber') };
        if (maxPages && num > maxPages)
          return { normalized: null, error: T.translate('case.pageExceedsDocumentPages') };
        return { normalized: String(num), error: null };
      }

      const rangeMatch = value.match(/^(\d+)\s*-\s*(\d+)$/);
      if (rangeMatch) {
        const start = parseInt(rangeMatch[1], 10);
        const end = parseInt(rangeMatch[2], 10);
        if (start < 1 || end < 1)
          return { normalized: null, error: T.translate('case.lessThanOnePageNumber') };
        if (end < start)
          return { normalized: null, error: T.translate('case.endPageGreaterThanStart') };
        if (maxPages && (start > maxPages || end > maxPages))
          return { normalized: null, error: T.translate('case.pageExceedsDocumentPages') };
        return { normalized: `${start}-${end}`, error: null };
      }

      return { normalized: null, error: T.translate('case.invalidPageNumberOrRange') };
    },
    [maxPages],
  );

  const parseNormalizedTokenInterval = useCallback((normalizedToken) => {
    if (!normalizedToken) return null;
    const rangeMatch = normalizedToken.match(/^(\d+)-(\d+)$/);
    if (rangeMatch) {
      const start = parseInt(rangeMatch[1], 10);
      const end = parseInt(rangeMatch[2], 10);
      if (Number.isFinite(start) && Number.isFinite(end)) {
        return { start, end };
      }
      return null;
    }
    if (/^\d+$/.test(normalizedToken)) {
      const page = parseInt(normalizedToken, 10);
      if (Number.isFinite(page)) {
        return { start: page, end: page };
      }
    }
    return null;
  }, []);

  const buildPageSetFromEntries = useCallback(
    (entries) => {
      const selection = new Set();
      (entries || []).forEach((entry) => {
        const rawValue = entry?.value || '';
        const parsed = parsePageToken(rawValue);
        if (!parsed.normalized) return;
        const rangeMatch = parsed.normalized.match(/^(\d+)-(\d+)$/);
        if (rangeMatch) {
          const start = parseInt(rangeMatch[1], 10);
          const end = parseInt(rangeMatch[2], 10);
          if (!Number.isFinite(start) || !Number.isFinite(end)) return;
          for (let page = start; page <= end; page += 1) {
            selection.add(page);
          }
          return;
        }
        const single = parseInt(parsed.normalized, 10);
        if (Number.isFinite(single)) selection.add(single);
      });
      return selection;
    },
    [parsePageToken],
  );

  const buildRangesFromPageSet = useCallback((pageSet) => {
    const pages = Array.from(pageSet).filter(Number.isFinite).sort((a, b) => a - b);
    if (!pages.length) return [];
    const ranges = [];
    let rangeStart = pages[0];
    let previous = pages[0];
    for (let i = 1; i < pages.length; i += 1) {
      const current = pages[i];
      if (current === previous + 1) {
        previous = current;
        continue;
      }
      ranges.push(rangeStart === previous ? `${rangeStart}` : `${rangeStart}-${previous}`);
      rangeStart = current;
      previous = current;
    }
    ranges.push(rangeStart === previous ? `${rangeStart}` : `${rangeStart}-${previous}`);
    return ranges;
  }, []);

  const normalizeFormFromPageSet = useCallback(
    (pageSet, invalidEntries = []) => {
      const ranges = buildRangesFromPageSet(pageSet);
      const mergedValues = [
        ...ranges.map((value) => ({ value })),
        ...invalidEntries.map((entry) => ({ value: entry.value })),
      ];
      if (mergedValues.length === 0) {
        replace([{ value: '' }]);
      } else {
        replace(mergedValues);
      }
    },
    [buildRangesFromPageSet, replace],
  );

  const buildParsedEntries = useCallback(
    (entries = []) => {
      const acceptedIntervals = [];
      return entries.map((entry) => {
        const rawValue = entry?.value || '';
        const parsed = parsePageToken(rawValue);
        const interval = parseNormalizedTokenInterval(parsed.normalized);
        let error = parsed.error;

        // Keep each row unique and non-overlapping to avoid ambiguous remove-page UI.
        if (!error && interval) {
          const overlapsExisting = acceptedIntervals.some(
            ([existingStart, existingEnd]) =>
              interval.start <= existingEnd && interval.end >= existingStart,
          );
          if (overlapsExisting) {
            error = T.translate('case.duplicateOrOverlappingPageSelection');
          } else {
            acceptedIntervals.push([interval.start, interval.end]);
          }
        }

        return {
          rawValue,
          normalized: parsed.normalized,
          error,
          interval,
        };
      });
    },
    [parsePageToken, parseNormalizedTokenInterval],
  );

  const parsedEntries = useMemo(
    () => buildParsedEntries(formValues?.pagesToRemove || []),
    [buildParsedEntries, formValues?.pagesToRemove],
  );

  const buildRemovedIntervals = useCallback(
    (entries = []) => {
      const intervals = buildParsedEntries(entries)
        .filter((entry) => entry.interval && !entry.error)
        .map((entry) => [entry.interval.start, entry.interval.end]);

      intervals.sort((a, b) => a[0] - b[0]);
      const merged = [];
      intervals.forEach(([start, end]) => {
        if (!merged.length || start > merged[merged.length - 1][1] + 1) {
          merged.push([start, end]);
        } else {
          merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], end);
        }
      });
      return merged;
    },
    [buildParsedEntries],
  );

  const removedIntervals = useMemo(
    () => buildRemovedIntervals(formValues?.pagesToRemove || []),
    [buildRemovedIntervals, formValues?.pagesToRemove],
  );

  const numericSelections = useMemo(() => {
    const selection = new Set();
    removedIntervals.forEach(([start, end]) => {
      for (let page = start; page <= end; page += 1) {
        selection.add(page);
      }
    });
    return selection;
  }, [removedIntervals]);

  const hasValidSelection = removedIntervals.length > 0;
  const hasInvalidSelection = parsedEntries.some(
    (entry) => entry.rawValue.trim() && entry.error,
  );
  const hasPageFieldErrors = Array.isArray(errors?.pagesToRemove)
    ? errors.pagesToRemove.some((entry) => !!entry?.value)
    : false;

  const totalPagesSelected = useMemo(
    () => removedIntervals.reduce((total, [start, end]) => total + (end - start + 1), 0),
    [removedIntervals],
  );

  const areAllPagesSelected = useMemo(() => {
    if (!Number.isFinite(maxPages) || maxPages < 1) return false;
    return totalPagesSelected === maxPages;
  }, [maxPages, totalPagesSelected]);

  const pagesToRemoveDisplay = useMemo(
    () =>
      removedIntervals.map(([start, end]) => (start === end ? `${start}` : `${start}-${end}`)),
    [removedIntervals],
  );

  const remainingPages = useMemo(() => {
    if (!Number.isFinite(maxPages)) return null;
    return maxPages - totalPagesSelected;
  }, [maxPages, totalPagesSelected]);

  const leavesAtLeastOnePage = useMemo(() => {
    if (!Number.isFinite(maxPages)) return true;
    return remainingPages >= 1;
  }, [maxPages, remainingPages]);

  const canSubmit =
    hasValidSelection &&
    !hasInvalidSelection &&
    !hasPageFieldErrors &&
    !hasNewDocConfigError &&
    leavesAtLeastOnePage &&
    !isSubmitting &&
    !isRefreshingFile &&
    removeRequestStatus === 'idle';

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      isModalOpenRef.current = false;
    };
  }, []);

  const pageMapPages = useMemo(() => {
    if (!Number.isFinite(maxPages) || maxPages < 1) return [];
    return Array.from({ length: maxPages }, (_, index) => index + 1);
  }, [maxPages]);

  const updateCurrentPreviewPage = useCallback(
    (rawPage) => {
      if (!isMountedRef.current || !isModalOpenRef.current) return;
      const parsedPage = Number(rawPage);
      if (!Number.isFinite(parsedPage) || parsedPage < 1) return;
      const maxPageNumber = Number.isFinite(maxPages) ? maxPages : parsedPage;
      const nextPage = Math.min(Math.max(parsedPage, 1), maxPageNumber);
      setCurrentPreviewPage((previousPage) =>
        previousPage === nextPage ? previousPage : nextPage,
      );
    },
    [maxPages],
  );

  const getSafeViewerPageNumber = useCallback((viewerControl, fallback = 1) => {
    try {
      const pageNumber = Number(viewerControl?.pageNumber);
      if (Number.isFinite(pageNumber) && pageNumber >= 1) return pageNumber;
    } catch (error) {
      // viewer may be disposed between effects while modal is closing
    }
    return fallback;
  }, []);

  const getSafeViewerPageCount = useCallback(
    (viewerControl) => {
      try {
        const pageCount = Number(viewerControl?.pageCount);
        if (Number.isFinite(pageCount) && pageCount >= 1) return pageCount;
      } catch (error) {
        // viewer may be disposed between effects while modal is closing
      }
      return Number.isFinite(maxPages) ? maxPages : 0;
    },
    [maxPages],
  );

  const handleGoToPage = useCallback((pageNumber) => {
    if (fileViewerRef.current && fileViewerRef.current.goToPage) {
      fileViewerRef.current.goToPage(pageNumber);
    } else if (fileViewerRef.current && fileViewerRef.current.scrollToPage) {
      fileViewerRef.current.scrollToPage(pageNumber);
    }
  }, []);

  const handlePageInputKeyDown = useCallback(
    (event, index, hasError) => {
      if (event.key !== 'Enter') return;

      // Keep Enter scoped to this modal input instead of triggering modal save.
      event.preventDefault();
      event.stopPropagation();

      if (hasError) return;

      const currentValue = (event.currentTarget?.value || '').trim();
      if (!currentValue) return;

      const currentEntries = getValues('pagesToRemove') || [];
      const isLastEntry = index === currentEntries.length - 1;
      if (!isLastEntry) return;

      append(
        { value: '' },
        {
          shouldFocus: true,
        },
      );
    },
    [append, getValues],
  );

  const applyPageRangeSelection = useCallback(
    (startPage, endPage, shouldSelect = true) => {
      if (!Number.isFinite(startPage) || !Number.isFinite(endPage)) return;
      const shouldAdd = shouldSelect !== false;

      let safeStart = Math.max(1, Math.floor(startPage));
      let safeEnd = Math.max(1, Math.floor(endPage));
      if (Number.isFinite(maxPages)) {
        safeStart = Math.min(maxPages, safeStart);
        safeEnd = Math.min(maxPages, safeEnd);
      }

      const rangeStart = Math.min(safeStart, safeEnd);
      const rangeEnd = Math.max(safeStart, safeEnd);

      const currentValues = getValues('pagesToRemove') || [];
      const invalidEntries = currentValues.filter((entry) => {
        const rawValue = entry?.value || '';
        if (!rawValue.trim()) return false;
        return parsePageToken(rawValue).error;
      });

      const pageSet = buildPageSetFromEntries(currentValues);
      for (let page = rangeStart; page <= rangeEnd; page += 1) {
        if (shouldAdd) {
          pageSet.add(page);
        } else {
          pageSet.delete(page);
        }
      }

      normalizeFormFromPageSet(pageSet, invalidEntries);
    },
    [buildPageSetFromEntries, getValues, maxPages, normalizeFormFromPageSet, parsePageToken],
  );

  const updatePageMapSelectionFromPointer = useCallback(
    (clientX, clientY) => {
      if (!isMapDraggingRef.current) return;
      if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;
      if (typeof mapDragShouldSelectRef.current !== 'boolean') return;

      const pageMapContainer = pageMapContainerRef.current;
      if (!pageMapContainer) return;

      const elementAtPoint = document.elementFromPoint(clientX, clientY);
      const pageButton = elementAtPoint?.closest?.('[data-page-map-page-number]');
      if (!pageButton || !pageMapContainer.contains(pageButton)) return;

      const pageNumber = Number(pageButton.getAttribute('data-page-map-page-number'));
      if (!Number.isFinite(pageNumber)) return;
      if (mapDragCurrentPageRef.current === pageNumber) return;

      mapDragCurrentPageRef.current = pageNumber;
      applyPageRangeSelection(
        mapDragStartPageRef.current,
        pageNumber,
        mapDragShouldSelectRef.current,
      );
    },
    [applyPageRangeSelection],
  );

  const stopPageMapAutoScroll = useCallback(() => {
    if (pageMapAutoScrollRafRef.current) {
      cancelAnimationFrame(pageMapAutoScrollRafRef.current);
    }
    pageMapAutoScrollRafRef.current = null;
  }, []);

  const runPageMapAutoScroll = useCallback(() => {
    pageMapAutoScrollRafRef.current = null;
    if (!isMapDraggingRef.current) return;

    const pageMapContainer = pageMapContainerRef.current;
    const { x, y } = pageMapPointerPositionRef.current;
    if (!pageMapContainer || !Number.isFinite(x) || !Number.isFinite(y)) return;

    const rect = pageMapContainer.getBoundingClientRect();
    const horizontalPadding = 24;
    if (x < rect.left - horizontalPadding || x > rect.right + horizontalPadding) return;

    const edgeThreshold = Math.min(32, Math.max(18, rect.height / 3));
    let scrollDelta = 0;

    if (y < rect.top + edgeThreshold) {
      const intensity = (rect.top + edgeThreshold - y) / edgeThreshold;
      scrollDelta = -Math.max(4, Math.round(intensity * 16));
    } else if (y > rect.bottom - edgeThreshold) {
      const intensity = (y - (rect.bottom - edgeThreshold)) / edgeThreshold;
      scrollDelta = Math.max(4, Math.round(intensity * 16));
    }

    if (!scrollDelta) return;

    const previousScrollTop = pageMapContainer.scrollTop;
    pageMapContainer.scrollTop += scrollDelta;

    if (pageMapContainer.scrollTop !== previousScrollTop) {
      updatePageMapSelectionFromPointer(x, y);
      pageMapAutoScrollRafRef.current = requestAnimationFrame(runPageMapAutoScroll);
    }
  }, [stopPageMapAutoScroll, updatePageMapSelectionFromPointer]);

  const syncPageMapAutoScroll = useCallback(
    (clientX, clientY) => {
      pageMapPointerPositionRef.current = { x: clientX, y: clientY };

      const pageMapContainer = pageMapContainerRef.current;
      if (!pageMapContainer || !isMapDraggingRef.current) {
        stopPageMapAutoScroll();
        return;
      }

      const rect = pageMapContainer.getBoundingClientRect();
      const edgeThreshold = Math.min(32, Math.max(18, rect.height / 3));
      const horizontalPadding = 24;
      const shouldAutoScroll =
        clientX >= rect.left - horizontalPadding &&
        clientX <= rect.right + horizontalPadding &&
        (clientY < rect.top + edgeThreshold || clientY > rect.bottom - edgeThreshold);

      if (shouldAutoScroll) {
        if (!pageMapAutoScrollRafRef.current) {
          pageMapAutoScrollRafRef.current = requestAnimationFrame(runPageMapAutoScroll);
        }
        return;
      }

      stopPageMapAutoScroll();
    },
    [runPageMapAutoScroll, stopPageMapAutoScroll],
  );

  const clearPageMapDragState = useCallback(() => {
    isMapDraggingRef.current = false;
    mapDragStartPageRef.current = null;
    mapDragCurrentPageRef.current = null;
    mapDragShouldSelectRef.current = null;
    pageMapPointerPositionRef.current = { x: null, y: null };
    stopPageMapAutoScroll();
  }, [stopPageMapAutoScroll]);

  useEffect(() => {
    if (!show) return undefined;

    const handlePointerEnd = () => {
      clearPageMapDragState();
    };

    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);
    return () => {
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerEnd);
    };
  }, [clearPageMapDragState, show]);

  useEffect(() => {
    if (!show) return undefined;

    const handlePointerMove = (event) => {
      if (!isMapDraggingRef.current) return;

      updatePageMapSelectionFromPointer(event.clientX, event.clientY);
      syncPageMapAutoScroll(event.clientX, event.clientY);
    };

    window.addEventListener('pointermove', handlePointerMove);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      stopPageMapAutoScroll();
    };
  }, [show, stopPageMapAutoScroll, syncPageMapAutoScroll, updatePageMapSelectionFromPointer]);

  const handlePageMapPointerDown = useCallback(
    (event, pageNumber) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      event.preventDefault();

      const isCurrentlySelected = numericSelectionsRef.current.has(pageNumber);
      const shouldSelect = !isCurrentlySelected;

      isMapDraggingRef.current = true;
      mapDragStartPageRef.current = pageNumber;
      mapDragCurrentPageRef.current = pageNumber;
      mapDragShouldSelectRef.current = shouldSelect;
      syncPageMapAutoScroll(event.clientX, event.clientY);

      handleGoToPage(pageNumber);
      applyPageRangeSelection(pageNumber, pageNumber, shouldSelect);
    },
    [applyPageRangeSelection, handleGoToPage, syncPageMapAutoScroll],
  );

  const handlePageMapPointerEnter = useCallback(
    (pageNumber) => {
      if (!isMapDraggingRef.current) return;
      if (!Number.isFinite(pageNumber)) return;
      if (mapDragCurrentPageRef.current === pageNumber) return;
      if (typeof mapDragShouldSelectRef.current !== 'boolean') return;

      mapDragCurrentPageRef.current = pageNumber;
      applyPageRangeSelection(
        mapDragStartPageRef.current,
        pageNumber,
        mapDragShouldSelectRef.current,
      );
    },
    [applyPageRangeSelection],
  );

  const handlePageMapClick = useCallback(
    (event, pageNumber) => {
      // Pointer interactions are handled in onPointerDown / onPointerEnter.
      if (event?.detail !== 0) return;
      const shouldSelect = !numericSelectionsRef.current.has(pageNumber);
      handleGoToPage(pageNumber);
      applyPageRangeSelection(pageNumber, pageNumber, shouldSelect);
    },
    [applyPageRangeSelection, handleGoToPage],
  );

  const handlePageMapKeyDown = useCallback(
    (event, pageNumber) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();
      const shouldSelect = !numericSelectionsRef.current.has(pageNumber);
      handleGoToPage(pageNumber);
      applyPageRangeSelection(pageNumber, pageNumber, shouldSelect);
    },
    [applyPageRangeSelection, handleGoToPage],
  );

  const canSelectAllFromPageMap = Number.isFinite(maxPages) && maxPages > 0;

  const handleSelectAllFromPageMap = useCallback(() => {
    if (!Number.isFinite(maxPages) || maxPages < 1) return;

    const currentValues = getValues('pagesToRemove') || [];
    const invalidEntries = currentValues.filter((entry) => {
      const rawValue = entry?.value || '';
      if (!rawValue.trim()) return false;
      return parsePageToken(rawValue).error;
    });

    const pageSet = new Set();
    if (!areAllPagesSelected) {
      for (let page = 1; page <= maxPages; page += 1) {
        pageSet.add(page);
      }
    }

    normalizeFormFromPageSet(pageSet, invalidEntries);
  }, [areAllPagesSelected, getValues, maxPages, normalizeFormFromPageSet, parsePageToken]);

  const togglePageSelection = useCallback(
    (pageNumber, checked) => {
      if (!Number.isFinite(pageNumber)) return;
      if (Number.isFinite(maxPages) && pageNumber > maxPages) return;

      const currentValues = getValues('pagesToRemove') || [];
      const invalidEntries = currentValues.filter((entry) => {
        const rawValue = entry?.value || '';
        if (!rawValue.trim()) return false;
        return parsePageToken(rawValue).error;
      });

      const pageSet = buildPageSetFromEntries(currentValues);
      if (checked) {
        pageSet.add(pageNumber);
      } else {
        pageSet.delete(pageNumber);
      }

      normalizeFormFromPageSet(pageSet, invalidEntries);
    },
    [buildPageSetFromEntries, getValues, maxPages, normalizeFormFromPageSet, parsePageToken],
  );

  useEffect(() => {
    checkboxHandlerRef.current = (pageNumber, checked) => {
      togglePageSelection(pageNumber, checked);
    };
  }, [togglePageSelection]);

  const syncCheckboxStates = useCallback(() => {
    const wrapper = viewerWrapperRef.current;
    const scope = wrapper || document;
    const inputs = scope.querySelectorAll(`input.${CHECKBOX_INPUT_CLASS}`);
    inputs.forEach((input) => {
      const pageNumber = parseInt(input.dataset.pageNumber || '', 10);
      if (!Number.isFinite(pageNumber)) return;
      input.checked = numericSelectionsRef.current.has(pageNumber);
    });
  }, []);

  const getPageNumberFromElement = useCallback(
    (pageEl, index) => {
      const dataset = pageEl.dataset || {};
      const nested =
        pageEl.querySelector?.(
          '[data-pcc-pagename], [data-pcc-pagenumber], [data-pcc-page-number]',
        ) || null;
      const nestedDataset = nested?.dataset || {};
      const candidateAttrs = [
        dataset.pccPagenumber,
        dataset.pccPageNumber,
        dataset.pageNumber,
        dataset.page,
        dataset.pageindex,
        nestedDataset.pccPagenumber,
        nestedDataset.pccPageNumber,
        nestedDataset.pageNumber,
        nestedDataset.page,
        nestedDataset.pageindex,
        pageEl.getAttribute?.('data-pcc-pagenumber'),
        pageEl.getAttribute?.('data-pcc-page-number'),
        pageEl.getAttribute?.('data-page-number'),
        pageEl.getAttribute?.('data-page'),
        pageEl.getAttribute?.('data-pageindex'),
        nested?.getAttribute?.('data-pcc-pagenumber'),
        nested?.getAttribute?.('data-pcc-page-number'),
        nested?.getAttribute?.('data-pcc-pagename'),
        pageEl.getAttribute?.('aria-label'),
      ];

      for (const candidate of candidateAttrs) {
        if (!candidate) continue;
        const extracted = String(candidate).replace(/[^0-9]/g, '');
        if (extracted) {
          const parsed = parseInt(extracted, 10);
          if (Number.isFinite(parsed)) return parsed;
        }
      }

      const pageName =
        pageEl.getAttribute?.('data-pcc-pagename') ||
        nested?.getAttribute?.('data-pcc-pagename') ||
        '';
      if (pageName) {
        const extracted = pageName.replace(/[^0-9]/g, '');
        if (extracted) {
          const parsed = parseInt(extracted, 10);
          if (Number.isFinite(parsed)) return parsed;
        }
      }

      return index + 1;
    },
    [],
  );

  const resolvePageElement = useCallback((node) => {
    if (!node) return null;
    const candidate = node.closest?.('.pccPageListItem, .pccPage, .pcc-page') || node;
    if (candidate?.classList?.contains('pccPageListItemLoading')) return null;
    return candidate;
  }, []);

  const collectPageElements = useCallback(
    (root, wrapper) => {
      if (!root) return [];
      const selector =
        '.pccPageListItem, .pccPage, .pcc-page, [data-pcc-pagename], [data-pcc-pagenumber], [data-pcc-page-number]';
      const nodes = root.querySelectorAll(selector);
      const unique = new Map();
      nodes.forEach((node) => {
        const resolved = resolvePageElement(node);
        if (!resolved) return;
        if (wrapper && !wrapper.contains(resolved)) return;
        unique.set(resolved, resolved);
      });
      return Array.from(unique.values());
    },
    [resolvePageElement],
  );

  const ensureOverlayContainer = useCallback(() => {
    const wrapper = viewerWrapperRef.current;
    if (!wrapper) return null;
    if (!overlayContainerRef.current) {
      const existingStyle = document.getElementById('remove-pages-overlay-style');
      if (!existingStyle) {
        const style = document.createElement('style');
        style.id = 'remove-pages-overlay-style';
        style.textContent =
          '@keyframes remove-pages-spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}';
        document.head.appendChild(style);
      }
      const container = document.createElement('div');
      container.className = CHECKBOX_OVERLAY_CLASS;
      container.style.cssText =
        'position:absolute;inset:0;z-index:999;pointer-events:none;';
      if (typeof window !== 'undefined') {
        const computed = window.getComputedStyle(wrapper);
        if (computed.position === 'static') {
          wrapper.style.position = 'relative';
        }
      }
      wrapper.appendChild(container);
      overlayContainerRef.current = container;
    }
    return overlayContainerRef.current;
  }, []);

  const getBatchIndexForPage = useCallback((pageNumber) => {
    if (!Number.isFinite(pageNumber) || pageNumber < 1) return 0;
    return Math.floor((pageNumber - 1) / PAGE_BATCH_SIZE);
  }, []);

  const getBatchRange = useCallback(
    (batchIndex, pageCount) => {
      const safeCount = Number.isFinite(pageCount) ? pageCount : maxPages || 0;
      const start = batchIndex * PAGE_BATCH_SIZE + 1;
      const end = Math.min(safeCount, start + PAGE_BATCH_SIZE - 1);
      return { start, end };
    },
    [maxPages],
  );

  const showBatchSpinner = useCallback(
    (show) => {
      const overlay = ensureOverlayContainer();
      if (!overlay) return;
      if (!overlayBatchLoadingRef.current) {
        const spinner = document.createElement('div');
        spinner.className = CHECKBOX_SPINNER_CLASS;
        spinner.style.cssText =
          'position:absolute;top:12px;right:12px;width:22px;height:22px;border-radius:50%;border:3px solid rgba(0,0,0,0.2);border-top-color:#4567e5;animation:remove-pages-spin 0.8s linear infinite;display:none;pointer-events:none;background:transparent;';
        overlay.appendChild(spinner);
        overlayBatchLoadingRef.current = spinner;
      }
      overlayBatchLoadingRef.current.style.display = show ? 'block' : 'none';
    },
    [ensureOverlayContainer],
  );

  const scheduleOverlayRefresh = useCallback(() => {
    if (!isMountedRef.current || !isModalOpenRef.current) return;
    if (overlayUpdateRafRef.current) {
      cancelAnimationFrame(overlayUpdateRafRef.current);
    }
    overlayUpdateRafRef.current = requestAnimationFrame(() => {
      if (!isMountedRef.current || !isModalOpenRef.current) return;
      overlayUpdateRafRef.current = null;
      if (refreshOverlayPositionsRef.current) {
        refreshOverlayPositionsRef.current();
      }
    });
  }, []);

  const getOverlayCheckbox = useCallback(
    (pageNumber) => {
      const overlay = ensureOverlayContainer();
      if (!overlay) return null;
      if (overlayCheckboxesRef.current.has(pageNumber)) {
        return overlayCheckboxesRef.current.get(pageNumber);
      }

      const container = document.createElement('label');
      container.className = CHECKBOX_CONTAINER_CLASS;
      container.dataset.pageNumber = String(pageNumber);
      container.style.cssText =
        'position:absolute;display:inline-flex;align-items:center;gap:6px;padding:2px 6px;border-radius:4px;background:rgba(255,255,255,0.95);box-shadow:0 1px 2px rgba(0,0,0,0.2);font-size:12px;color:#333;pointer-events:auto;';

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.className = CHECKBOX_INPUT_CLASS;
      input.dataset.pageNumber = String(pageNumber);
      input.checked = numericSelectionsRef.current.has(pageNumber);
      input.style.pointerEvents = 'auto';
      input.addEventListener('change', (event) => {
        const checked = event.target.checked;
        checkboxHandlerRef.current(pageNumber, checked);
      });

      const label = document.createElement('span');
      label.textContent = `${T.translate('case.page')} ${pageNumber}`;

      container.appendChild(input);
      container.appendChild(label);
      overlay.appendChild(container);

      const entry = { container, input };
      overlayCheckboxesRef.current.set(pageNumber, entry);
      return entry;
    },
    [ensureOverlayContainer],
  );

  const getPageAttributes = useCallback(async (pageNumber) => {
    if (overlayPageAttributesRef.current.has(pageNumber)) {
      return overlayPageAttributesRef.current.get(pageNumber);
    }
    const viewerControl = previewViewerControlRef.current;
    if (!viewerControl?.requestPageAttributes) return null;
    try {
      const attrs = await viewerControl.requestPageAttributes(pageNumber);
      if (
        attrs &&
        Number.isFinite(attrs.width) &&
        Number.isFinite(attrs.height)
      ) {
        overlayPageAttributesRef.current.set(pageNumber, attrs);
        return attrs;
      }
      return null;
    } catch (error) {
      return null;
    }
  }, []);

  const computeBatchAttributes = useCallback(
    async (batchIndex, pageCount) => {
      if (!isMountedRef.current || !isModalOpenRef.current) return;
      if (overlayComputedBatchesRef.current.has(batchIndex)) return;
      if (overlayBatchInFlightRef.current === batchIndex) return;
      overlayBatchInFlightRef.current = batchIndex;
      showBatchSpinner(true);

      const viewerControl = previewViewerControlRef.current;
      if (!viewerControl?.requestPageAttributes) {
        showBatchSpinner(false);
        overlayBatchInFlightRef.current = null;
        return;
      }

      const { start, end } = getBatchRange(batchIndex, pageCount);
      const pageNumbers = [];
      for (let page = start; page <= end; page += 1) {
        if (!overlayPageAttributesRef.current.has(page)) {
          pageNumbers.push(page);
        }
      }

      const concurrency = 6;
      for (let i = 0; i < pageNumbers.length; i += concurrency) {
        if (!isMountedRef.current || !isModalOpenRef.current) {
          overlayBatchInFlightRef.current = null;
          showBatchSpinner(false);
          return;
        }
        const slice = pageNumbers.slice(i, i + concurrency);
        await Promise.all(
          slice.map(async (pageNumber) => {
            try {
              const attrs = await viewerControl.requestPageAttributes(pageNumber);
              if (
                attrs &&
                Number.isFinite(attrs.width) &&
                Number.isFinite(attrs.height)
              ) {
                overlayPageAttributesRef.current.set(pageNumber, attrs);
              }
            } catch (error) {
              // ignore individual failures
            }
          }),
        );
      }

      overlayComputedBatchesRef.current.add(batchIndex);
      overlayBatchInFlightRef.current = null;
      showBatchSpinner(false);
      scheduleOverlayRefresh();
    },
    [getBatchRange, scheduleOverlayRefresh, showBatchSpinner],
  );

  const refreshOverlayPositions = useCallback(async () => {
    const viewerControl = previewViewerControlRef.current;
    const wrapper = viewerWrapperRef.current;
    const overlay = ensureOverlayContainer();
    if (!viewerControl || !wrapper || !overlay) return;

    const pageCount = getSafeViewerPageCount(viewerControl);
    if (!pageCount || !Number.isFinite(pageCount)) return;
    const wrapperRect = wrapper.getBoundingClientRect();

    const safePage = getSafeViewerPageNumber(viewerControl, 1);
    const currentPage = Math.min(Math.max(safePage, 1), pageCount);
    const batchIndex = getBatchIndexForPage(currentPage);
    if (!overlayComputedBatchesRef.current.has(batchIndex)) {
      overlayCheckboxesRef.current.forEach((entry) => {
        entry.container.style.display = 'none';
      });
      computeBatchAttributes(batchIndex, pageCount);
      return;
    }

    const { start: startPage, end: endPage } = getBatchRange(batchIndex, pageCount);

    overlayCheckboxesRef.current.forEach((entry, pageNumber) => {
      if (pageNumber < startPage || pageNumber > endPage) {
        entry.container.style.display = 'none';
      }
    });

    const tasks = [];
    for (let pageNumber = startPage; pageNumber <= endPage; pageNumber += 1) {
      tasks.push(
        (async () => {
          const entry = getOverlayCheckbox(pageNumber);
          if (!entry) return;

          const attrs = await getPageAttributes(pageNumber);
          if (!attrs) {
            entry.container.style.display = 'none';
            return;
          }
          if (
            !viewerControl.convertPageToWindowCoordinates ||
            !Number.isFinite(pageNumber) ||
            !Number.isFinite(attrs.width)
          ) {
            entry.container.style.display = 'none';
            return;
          }
          let coordsRight = null;
          let coordsTop = null;
          let coordsBottom = null;
          try {
            coordsRight = viewerControl.convertPageToWindowCoordinates(pageNumber, {
              x: attrs.width,
              y: 0,
            });
            coordsTop = viewerControl.convertPageToWindowCoordinates(pageNumber, {
              x: 0,
              y: 0,
            });
            coordsBottom = viewerControl.convertPageToWindowCoordinates(pageNumber, {
              x: 0,
              y: attrs.height,
            });
          } catch (error) {
            coordsRight = null;
            coordsTop = null;
            coordsBottom = null;
          }
          if (
            !coordsRight ||
            coordsRight.clientX === undefined ||
            coordsRight.clientY === undefined ||
            !coordsTop ||
            coordsTop.clientY === undefined ||
            !coordsBottom ||
            coordsBottom.clientY === undefined
          ) {
            entry.container.style.display = 'none';
            return;
          }

          const top = coordsTop.clientY - wrapperRect.top;
          const bottom = coordsBottom.clientY - wrapperRect.top;
          if (bottom < -60 || top > wrapperRect.height + 60) {
            entry.container.style.display = 'none';
            return;
          }

          const left = coordsRight.clientX - wrapperRect.left - 8;

          entry.container.style.display = 'inline-flex';
          entry.container.style.left = `${left}px`;
          entry.container.style.top = `${top + 8}px`;
          entry.container.style.transform = 'translate(-100%, 0)';
        })(),
      );
    }

    await Promise.all(tasks);
    syncCheckboxStates();
  }, [
    computeBatchAttributes,
    ensureOverlayContainer,
    getOverlayCheckbox,
    getPageAttributes,
    getBatchIndexForPage,
    getBatchRange,
    getSafeViewerPageCount,
    getSafeViewerPageNumber,
    maxPages,
    syncCheckboxStates,
  ]);

  useEffect(() => {
    refreshOverlayPositionsRef.current = refreshOverlayPositions;
  }, [refreshOverlayPositions]);

  const injectCheckboxes = useCallback(() => {
    if (useViewerOverlay) return 0;
    const wrapper = viewerWrapperRef.current;
    const pageList =
      wrapper?.querySelector?.('[data-pcc-pagelist]') || wrapper?.querySelector?.('.pcc-page-list');
    const shadowRoot = pageList?.shadowRoot || null;
    const scopedRoot = shadowRoot || pageList || wrapper;
    let pageElements = scopedRoot ? collectPageElements(scopedRoot, wrapper) : [];
    if (!pageElements || pageElements.length === 0) {
      const scopedMatches = document.querySelector('[data-remove-pages-viewer]')
        ? collectPageElements(document.querySelector('[data-remove-pages-viewer]'), wrapper)
        : [];
      const fallbackMatches =
        scopedMatches.length > 0 ? scopedMatches : collectPageElements(document, wrapper);
      pageElements = fallbackMatches;
    }

    if (!pageElements || pageElements.length === 0) {
      const iframeTargets = wrapper
        ? Array.from(wrapper.querySelectorAll('iframe'))
        : Array.from(document.querySelectorAll('iframe'));
      for (const iframe of iframeTargets) {
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!iframeDoc) continue;
          const iframePages = collectPageElements(iframeDoc, null);
          if (iframePages && iframePages.length > 0) {
            pageElements = iframePages;
            break;
          }
        } catch (error) {
          // ignore cross-origin frames
        }
      }
    }

    if (!pageElements || pageElements.length === 0) {
      setDebugCheckboxStatus('No page elements found in viewer markup.');
      return 0;
    }

    let insertedCount = 0;
    pageElements.forEach((pageEl, index) => {
      if (pageEl.querySelector(`.${CHECKBOX_CONTAINER_CLASS}`)) return;
      const pageNumber = getPageNumberFromElement(pageEl, index);
      if (!Number.isFinite(pageNumber)) return;

      if (typeof window !== 'undefined') {
        const computed = window.getComputedStyle(pageEl);
        if (computed.position === 'static') {
          pageEl.style.position = 'relative';
        }
      }

      const container = document.createElement('label');
      container.className = CHECKBOX_CONTAINER_CLASS;
      container.style.cssText =
        'position:absolute;top:8px;right:8px;z-index:999;display:inline-flex;align-items:center;gap:6px;padding:2px 6px;border-radius:4px;background:rgba(255,255,255,0.9);box-shadow:0 1px 2px rgba(0,0,0,0.15);font-size:12px;color:#333;pointer-events:auto;';

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.className = CHECKBOX_INPUT_CLASS;
      input.dataset.pageNumber = String(pageNumber);
      input.checked = numericSelectionsRef.current.has(pageNumber);
      input.style.pointerEvents = 'auto';
      input.addEventListener('change', (event) => {
        const checked = event.target.checked;
        checkboxHandlerRef.current(pageNumber, checked);
      });

      const label = document.createElement('span');
      label.textContent = `${T.translate('case.page')} ${pageNumber}`;

      container.appendChild(input);
      container.appendChild(label);
      pageEl.appendChild(container);
      insertedCount += 1;
    });

    syncCheckboxStates();
    if (insertedCount > 0) {
      setDebugCheckboxStatus('');
    } else {
      setDebugCheckboxStatus(`Found ${pageElements.length} page nodes but inserted 0 checkboxes.`);
    }
    return insertedCount;
  }, [collectPageElements, getPageNumberFromElement, syncCheckboxStates, useViewerOverlay]);

  useEffect(() => {
    numericSelectionsRef.current = numericSelections;
    syncCheckboxStates();
    scheduleOverlayRefresh();
  }, [numericSelections, scheduleOverlayRefresh, syncCheckboxStates]);

  useEffect(() => {
    if (!viewerReady || !show || useViewerOverlay) return;
    if (typeof MutationObserver === 'undefined') return;

    const wrapper = viewerWrapperRef.current;
    const pageList =
      wrapper?.querySelector('[data-pcc-pagelist]') || wrapper?.querySelector('.pcc-page-list');
    const observeTarget = pageList || wrapper || document.body;
    if (!observeTarget) return;

    if (checkboxObserverRef.current) {
      checkboxObserverRef.current.disconnect();
    }

    const observer = new MutationObserver(() => {
      injectCheckboxes();
    });
    observer.observe(observeTarget, { childList: true, subtree: true });
    checkboxObserverRef.current = observer;

    const maxAttempts = 12;
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      const inserted = injectCheckboxes();
      if (inserted > 0) {
        setDebugCheckboxStatus('');
      } else if (attempts === maxAttempts) {
        setDebugCheckboxStatus('Unable to attach checkboxes to preview pages.');
      }
      if (inserted > 0 || attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 400);

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [injectCheckboxes, show, viewerReady, file?.id, useViewerOverlay]);

  useEffect(() => {
    if (!viewerReady || !show) return;
    let cancelled = false;

    const tryResolveViewer = (attempt = 0) => {
      if (cancelled) return;
      const control = fileViewerRef.current?.getViewerControl?.();
      if (control) {
        previewViewerControlRef.current = control;
        const safePageCount = getSafeViewerPageCount(control);
        if (Number.isFinite(safePageCount) && safePageCount > 0) {
          setViewerPageCount((prevCount) =>
            prevCount === safePageCount ? prevCount : safePageCount,
          );
        }
        updateCurrentPreviewPage(getSafeViewerPageNumber(control, 1));
        setUseViewerOverlay(true);
        setDebugCheckboxStatus('');
        return;
      }
      if (attempt < 10) {
        setTimeout(() => tryResolveViewer(attempt + 1), 250);
      }
    };

    tryResolveViewer();
    return () => {
      cancelled = true;
    };
  }, [
    file?.id,
    getSafeViewerPageCount,
    getSafeViewerPageNumber,
    show,
    updateCurrentPreviewPage,
    viewerReady,
  ]);

  useEffect(() => {
    if (!useViewerOverlay || !viewerReady || !show) return;
    const viewerControl = previewViewerControlRef.current;
    if (!viewerControl) return;

    let scrollContainer = getScrollContainer(viewerControl);
    const wrapper = viewerWrapperRef.current;
    if (scrollContainer && wrapper && !wrapper.contains(scrollContainer)) {
      scrollContainer = null;
    }
    const syncCurrentPageCount = () => {
      const safePageCount = getSafeViewerPageCount(viewerControl);
      if (Number.isFinite(safePageCount) && safePageCount > 0) {
        setViewerPageCount((prevCount) =>
          prevCount === safePageCount ? prevCount : safePageCount,
        );
      }
    };
    const syncCurrentPage = () => {
      const safePage = getSafeViewerPageNumber(viewerControl, currentPreviewPage || 1);
      updateCurrentPreviewPage(safePage);
    };

    const handleScroll = () => {
      syncCurrentPageCount();
      syncCurrentPage();
      scheduleOverlayRefresh();
    };
    const handleUpdate = () => {
      syncCurrentPageCount();
      syncCurrentPage();
      scheduleOverlayRefresh();
    };

    scrollContainer?.addEventListener('scroll', handleScroll);
    viewerControl.on?.('ScaleChanged', handleUpdate);
    viewerControl.on?.('DocumentRotated', handleUpdate);
    viewerControl.on?.('PageChanged', handleUpdate);
    viewerControl.on?.('PageCountReady', handleUpdate);
    window.addEventListener('resize', handleUpdate);

    syncCurrentPageCount();
    syncCurrentPage();
    scheduleOverlayRefresh();

    return () => {
      scrollContainer?.removeEventListener('scroll', handleScroll);
      viewerControl.off?.('ScaleChanged', handleUpdate);
      viewerControl.off?.('DocumentRotated', handleUpdate);
      viewerControl.off?.('PageChanged', handleUpdate);
      viewerControl.off?.('PageCountReady', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [
    currentPreviewPage,
    getSafeViewerPageCount,
    getSafeViewerPageNumber,
    scheduleOverlayRefresh,
    show,
    updateCurrentPreviewPage,
    useViewerOverlay,
    viewerReady,
  ]);

  useEffect(() => {
    setViewerReady(false);
  }, [file?.id, viewerInstanceKey]);

  useEffect(() => {
    isModalOpenRef.current = show;
    if (!show) {
      pendingRemoveRequestIdRef.current += 1;
      if (overlayUpdateRafRef.current) {
        cancelAnimationFrame(overlayUpdateRafRef.current);
      }
      overlayUpdateRafRef.current = null;
      if (overlayContainerRef.current) {
        overlayContainerRef.current.remove();
      }
      overlayContainerRef.current = null;
      overlayCheckboxesRef.current.clear();
      overlayPageAttributesRef.current.clear();
      overlayComputedBatchesRef.current.clear();
      overlayBatchInFlightRef.current = null;
      overlayBatchLoadingRef.current = null;
      refreshOverlayPositionsRef.current = null;
      previewViewerControlRef.current = null;
      clearPageMapDragState();
      setUseViewerOverlay(false);
      setCurrentPreviewPage(1);
      setViewerPageCount(null);
      setIsSubmitting(false);
      setIsRefreshingFile(false);
      setDebugCheckboxStatus('');
      setRemoveRequestStatus('idle');
      setRemoveRequestMessage('');
      setRemoveResultLink('');
      removeRequestTrackingRef.current = {
        active: false,
        startedAtMs: 0,
        sourceFileId: null,
      };
      transientNotificationLogIndexRef.current = 0;
      notificationLogIndexRef.current = 0;
      reset({
        pagesToRemove: [{ value: '' }],
        createNewDocument: false,
        newDocName: suggestedNewDocName,
        folder: 'auto-generated',
      });
    }
  }, [clearPageMapDragState, reset, show, suggestedNewDocName]);

  useEffect(() => {
    if (!show || !selectedFileId) return;
    const effectiveFileId = selectedFolderFileId || selectedFileId;
    if (!effectiveFileId.startsWith('TV-')) return;
    let cancelled = false;

    setViewerPageCount(null);
    setViewerInstanceKey((value) => value + 1);
    setIsRefreshingFile(true);
    fetchFileDetails({
      fileId: selectedFileId,
      folderFileId: selectedFolderFileId,
      firstTrialBookOnly: true,
    })
      .then((res) => {
        if (cancelled || !res || res.error) return;
        setFile((prevFile) => ({ ...(prevFile || {}), ...res, id: res.id || selectedFileId }));
        setViewerInstanceKey((value) => value + 1);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsRefreshingFile(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fetchFileDetails, selectedFileId, selectedFolderFileId, show]);

  useEffect(() => {
    if (!show || removeRequestStatus !== 'processing') return;

    const transientList = Array.isArray(transientNotifications) ? transientNotifications : [];
    const notificationList = Array.isArray(notifications) ? notifications : [];

    let transientStartIndex = transientNotificationLogIndexRef.current;
    let notificationStartIndex = notificationLogIndexRef.current;

    if (transientStartIndex > transientList.length) transientStartIndex = transientList.length;
    if (notificationStartIndex > notificationList.length) {
      notificationStartIndex = notificationList.length;
    }

    if (process.env.NODE_ENV === 'development') {
      transientList.slice(transientStartIndex).forEach((notification) => {
        console.log('[ModalRemovePages] transient notification', notification);
      });
      notificationList.slice(notificationStartIndex).forEach((notification) => {
        console.log('[ModalRemovePages] notification', notification);
      });
    }

    transientNotificationLogIndexRef.current = transientList.length;
    notificationLogIndexRef.current = notificationList.length;
  }, [notifications, removeRequestStatus, show, transientNotifications]);

  useEffect(() => {
    if (!show || removeRequestStatus !== 'processing') return;
    if (!removeRequestTrackingRef.current.active) return;

    const latestMatchingNotification = findLatestRemoveNotification([
      ...(Array.isArray(transientNotifications) ? transientNotifications : []),
      ...(Array.isArray(notifications) ? notifications : []),
    ]);

    if (!latestMatchingNotification) return;

    const notificationLink = extractDocumentLink(latestMatchingNotification);
    const notificationIndicatesFailure = isRemovePagesFailure(latestMatchingNotification);
    if (notificationLink) {
      setRemoveResultLink(notificationLink);
    }
    setRemoveRequestMessage(
      getRemovePagesStatusMessage(latestMatchingNotification, {
        isFailure: notificationIndicatesFailure,
        fallbackMessage: notificationIndicatesFailure
          ? T.translate('feedback.removePagesDocumentFailure') ||
            'An issue occurred while removing pages. Please try again.'
          : T.translate('generic.removePagesReadyMessage') || 'Your updated document is ready.',
      }),
    );
    if (notificationIndicatesFailure) {
      setRemoveRequestStatus('error');
      removeRequestTrackingRef.current.active = false;
      return;
    }

    if (!notificationLink) return;

    setRemoveRequestStatus('ready');
    removeRequestTrackingRef.current.active = false;
  }, [
    extractDocumentLink,
    findLatestRemoveNotification,
    getRemovePagesStatusMessage,
    isRemovePagesFailure,
    notifications,
    removeRequestStatus,
    show,
    transientNotifications,
  ]);

  const handleGoToUpdatedDocument = useCallback(() => {
    if (!removeResultLink) return;
    const basePath = history.location.pathname.replace(/\/case.*/, ``);
    history.push(basePath + removeResultLink);
    handleClose();
  }, [handleClose, removeResultLink]);

  const handleRemovePages = handleSubmit(async () => {
    const liveEntries = getValues('pagesToRemove') || [];
    const liveParsedEntries = buildParsedEntries(liveEntries);
    const liveRemovedIntervals = buildRemovedIntervals(liveEntries);
    const requestFileId = selectedFileId || file?.fileId || file?.id;
    const liveHasInvalidSelection = liveParsedEntries.some(
      (entry) => entry.rawValue.trim() && entry.error,
    );
    const liveTotalPagesSelected = liveRemovedIntervals.reduce(
      (total, [start, end]) => total + (end - start + 1),
      0,
    );
    const liveLeavesAtLeastOnePage = !Number.isFinite(maxPages)
      ? true
      : maxPages - liveTotalPagesSelected >= 1;
    const pagesToRemove = liveRemovedIntervals.map(([start, end]) =>
      start === end ? `${start}` : `${start}-${end}`,
    );

    const shouldCreateNewDocument = !!getValues('createNewDocument');
    const payloadNewDocName = (getValues('newDocName') || '').trim();
    const selectedNewDocFolderId = getValues('folder');

    if (!requestFileId || pagesToRemove.length === 0 || liveHasInvalidSelection) {
      return;
    }
    if (!liveLeavesAtLeastOnePage) {
      return;
    }
    if (shouldCreateNewDocument && (!payloadNewDocName || !selectedNewDocFolderId)) {
      return;
    }

    const requestId = pendingRemoveRequestIdRef.current + 1;
    pendingRemoveRequestIdRef.current = requestId;
    if (isMountedRef.current) {
      setIsSubmitting(true);
      setRemoveRequestStatus('processing');
      setRemoveRequestMessage(
        T.translate('generic.removePagesProcessingMessage') ||
          'Your remove pages request is processing. You can close this window while we finish.',
      );
      setRemoveResultLink('');
      transientNotificationLogIndexRef.current = Array.isArray(transientNotifications)
        ? transientNotifications.length
        : 0;
      notificationLogIndexRef.current = Array.isArray(notifications) ? notifications.length : 0;
      removeRequestTrackingRef.current = {
        active: true,
        startedAtMs: Date.now(),
        sourceFileId: requestFileId,
      };
    }

    const isRequestActive = () =>
      isMountedRef.current &&
      isModalOpenRef.current &&
      pendingRemoveRequestIdRef.current === requestId;

    try {
      let payloadNewDoc = null;
      if (shouldCreateNewDocument) {
        let targetFolderId = selectedNewDocFolderId;

        if (!targetFolderId || targetFolderId === 'auto-generated') {
          const addFolderResponse = await dispatch(
            actions.addFolder(
              {
                name: autoGeneratedFolderName,
                type: 'team-bundle',
              },
              { suppressFeedback: true },
            ),
          );

          if (!addFolderResponse || !addFolderResponse.id) {
            throw new Error('Failed to create remove pages folder');
          }

          targetFolderId = addFolderResponse.id;

          try {
            await dispatch(actions.fetchContainer({ folder: { type: 'team-bundle' } }));
          } catch {
            // Sidebar refresh is best-effort and should not block the request.
          }
        }

        payloadNewDoc = {
          name: payloadNewDocName,
          folderId: targetFolderId,
        };
      }

      const response = await dispatch(
        actions.removePagesDocument({
          caseId: currentCaseId,
          fileId: requestFileId,
          pagesToRemove,
          ...(payloadNewDoc && { newDoc: payloadNewDoc }),
        }),
      );
      
      if (isRequestActive()) {
        const responseLink = extractDocumentLink(response);
        const responseIndicatesFailure = isRemovePagesFailure(response);
        if (responseLink) {
          setRemoveResultLink(responseLink);
        }

        if (responseIndicatesFailure) {
          removeRequestTrackingRef.current.active = false;
          setRemoveRequestStatus('error');
          setRemoveRequestMessage(
            getRemovePagesStatusMessage(response, {
              isFailure: true,
              fallbackMessage:
                T.translate('feedback.removePagesDocumentFailure') ||
                'An issue occurred while removing pages. Please try again.',
            }),
          );
          addToast(T.translate('feedback.removePagesDocumentFailure'), {
            appearance: 'error',
            autoDismiss: true,
          });
          return;
        }

        if (responseLink) {
          setRemoveRequestStatus('ready');
          setRemoveRequestMessage(
            T.translate('generic.removePagesReadyMessage') || 'Your updated document is ready.',
          );
          removeRequestTrackingRef.current.active = false;
        } else {
          setRemoveRequestStatus('processing');
        }
      }
    } catch (error) {
      if (isRequestActive()) {
        removeRequestTrackingRef.current.active = false;
        const errorLink = extractDocumentLink(error);
        if (errorLink) {
          setRemoveResultLink(errorLink);
        }
        setRemoveRequestStatus('error');
        setRemoveRequestMessage(
          getRemovePagesStatusMessage(error, {
            isFailure: true,
            fallbackMessage:
              T.translate('feedback.removePagesDocumentFailure') ||
              'An issue occurred while removing pages. Please try again.',
          }),
        );
        addToast(T.translate('feedback.removePagesDocumentFailure'), {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    } finally {
      if (isMountedRef.current && pendingRemoveRequestIdRef.current === requestId) {
        setIsSubmitting(false);
      }
    }
  });

  if (isAsyncStatusView) {
    const isProcessing = removeRequestStatus === 'processing';
    const isError = removeRequestStatus === 'error';
    let asyncTitle = T.translate('generic.removePagesReadyTitle') || 'Updated document ready';
    if (isProcessing) {
      asyncTitle = T.translate('generic.removePagesProcessingTitle') || 'Request in progress';
    } else if (isError) {
      asyncTitle = T.translate('generic.errorOccurred');
    }

    return (
      <Modal
        show={show}
        handleClose={handleClose}
        title={`${T.translate('generic.removePages')} - ${file?.id || ''}`}
        wide
        draggableDialog
      >
        <div
          style={{
            minHeight: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem',
          }}
        >
          <div style={{ maxWidth: '560px', textAlign: 'center' }}>
            <div style={{ marginBottom: '0.9rem' }}>
              {isProcessing ? (
                <CircularProgress size={40} />
              ) : isError ? (
                <ErrorOutline sx={{ fontSize: '2.2rem', color: '#c62828' }} />
              ) : (
                <CheckCircleOutline sx={{ fontSize: '2.2rem', color: '#2e7d32' }} />
              )}
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#343a40', marginBottom: '0.6rem' }}>
              {asyncTitle}
            </div>
            <div style={{ fontSize: '0.95rem', color: '#5f6378', marginBottom: '1rem' }}>
              {removeRequestMessage ||
                (T.translate('generic.removePagesProcessingMessage') ||
                  'Your remove pages request is processing. You can close this window while we finish.')}
            </div>
            {isProcessing && (
              <div style={{ fontSize: '0.85rem', color: '#777', marginBottom: '1rem' }}>
                {T.translate('generic.removePagesWaitingForLink') ||
                  'Waiting for the updated document link...'}
              </div>
            )}
            {!isProcessing && !!removeResultLink && (
              <Button variant="contained" onClick={handleGoToUpdatedDocument}>
                {T.translate('generic.goToDocument') || 'Go to document'}
              </Button>
            )}
            {isError && !removeResultLink && (
              <Button
                variant="outlined"
                onClick={() => {
                  setRemoveRequestStatus('idle');
                  setRemoveRequestMessage('');
                  setRemoveResultLink('');
                  removeRequestTrackingRef.current.active = false;
                }}
              >
                {T.translate('generic.tryAgain')}
              </Button>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      handleClose={handleClose}
      title={`${T.translate('generic.removePages')} - ${file?.id || ''}`}
      handleSave={handleRemovePages}
      saveTitle={T.translate('generic.removePages')}
      disableSave={!canSubmit}
      loadingBtn={isSubmitting}
      wide
      draggableDialog
      fullHeight
      footerLeftContent={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
          <WarningAmber sx={{ fontSize: '1rem', color: '#C6922C' }} />
          <span>{T.translate('generic.removePagesWarning')}</span>
        </span>
      }
    >
      <Split
        sizes={[36, 64]}
        minSize={100}
        expandToMin={false}
        gutterSize={10}
        gutterAlign="center"
        snapOffset={30}
        dragInterval={1}
        direction="horizontal"
        cursor="col-resize"
        className="case-table-and-viewer"
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'auto',
            paddingRight: '1rem',
          }}
        >
          <div style={{ marginBottom: '1rem' }}>
            {pageMapPages.length > 0 && (
              <div
                style={{
                  border: '1px solid #d9dbe7',
                  borderRadius: '6px',
                  padding: '0.75rem',
                  marginBottom: '0.85rem',
                  backgroundColor: '#f8f9fd',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem',
                  }}
                >
                  <span
                    style={{
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      color: '#6c7396',
                    }}
                  >
                    PAGE MAP
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                    <span style={{ fontSize: '0.75rem', color: '#6b6f84' }}>
                      {T.translate('case.currentPage') || 'Current Page'}: {currentPreviewPage}
                    </span>
                    <Button
                      variant="text"
                      size="small"
                      disabled={!canSelectAllFromPageMap}
                      onClick={handleSelectAllFromPageMap}
                      sx={{
                        minWidth: 'auto',
                        padding: '2px 6px',
                        fontSize: '0.72rem',
                        textTransform: 'none',
                      }}
                    >
                      {areAllPagesSelected
                        ? T.translate('generic.deselectAll') || 'Deselect All'
                        : T.translate('generic.selectAllFiles') || 'Select All'}
                    </Button>
                  </div>
                </div>
                <div
                  ref={pageMapContainerRef}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(12px, 12px))',
                    gap: '3px',
                    maxHeight: '96px',
                    overflowY: 'auto',
                    padding: '1px',
                    borderRadius: '4px',
                  }}
                >
                  {pageMapPages.map((pageNumber) => {
                    const isRemoved = numericSelections.has(pageNumber);
                    const isCurrent = pageNumber === currentPreviewPage;
                    const backgroundColor = isRemoved ? '#e65b63' : '#e2e4ee';
                    const borderColor = isCurrent ? '#4e68de' : 'transparent';
                    return (
                      <button
                        key={`page-map-${pageNumber}`}
                        type="button"
                        onClick={(event) => handlePageMapClick(event, pageNumber)}
                        onPointerDown={(event) => handlePageMapPointerDown(event, pageNumber)}
                        onPointerEnter={() => handlePageMapPointerEnter(pageNumber)}
                        onPointerUp={clearPageMapDragState}
                        onKeyDown={(event) => handlePageMapKeyDown(event, pageNumber)}
                        data-page-map-page-number={pageNumber}
                        title={`Page ${pageNumber}`}
                        aria-label={`Page ${pageNumber}`}
                        aria-pressed={isRemoved}
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '3px',
                          border: `2px solid ${borderColor}`,
                          backgroundColor,
                          cursor: 'pointer',
                          padding: 0,
                          outline: 'none',
                          userSelect: 'none',
                          boxShadow: isCurrent ? '0 0 0 1px rgba(78, 104, 222, 0.25)' : 'none',
                          transition: 'background-color 0.1s ease, border-color 0.1s ease',
                        }}
                      />
                    );
                  })}
                </div>
                <div
                  style={{
                    marginTop: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    flexWrap: 'wrap',
                    fontSize: '0.78rem',
                    color: '#7a7f96',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span
                      style={{
                        width: '9px',
                        height: '9px',
                        borderRadius: '2px',
                        backgroundColor: '#e2e4ee',
                        display: 'inline-block',
                      }}
                    />
                    Keep
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span
                      style={{
                        width: '9px',
                        height: '9px',
                        borderRadius: '2px',
                        backgroundColor: '#e65b63',
                        display: 'inline-block',
                      }}
                    />
                    Remove
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span
                      style={{
                        width: '9px',
                        height: '9px',
                        borderRadius: '2px',
                        backgroundColor: '#e2e4ee',
                        border: '2px solid #4e68de',
                        display: 'inline-block',
                      }}
                    />
                    Current
                  </span>
                </div>
              </div>
            )}
            <div style={{ fontSize: '0.95rem', marginBottom: '0.35rem', color: '#4b4b4b' }}>
              {T.translate('generic.removePagesInstructions')}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#6b6b6b' }}>
              {T.translate('generic.removePagesRangeHint')}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#6b6b6b' }}>
              {T.translate('generic.removePagesPreviewTip')}
            </div>
            {file?.name && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6b6b6b' }}>
                <strong>{T.translate('common.file')}:</strong> {file.name}
                {Number.isFinite(maxPages) ? ` • ${T.translate('generic.pages')}: ${maxPages}` : ''}
              </div>
            )}
            {isRefreshingFile && (
              <div style={{ marginTop: '0.35rem', fontSize: '0.8rem', color: '#6b6f84' }}>
                Refreshing document details...
              </div>
            )}
            <div
              style={{
                marginTop: '0.8rem',
                border: '1px solid #d9dbe7',
                borderRadius: '6px',
                padding: '0.75rem',
                backgroundColor: '#f8f9fd',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createNewDocument}
                    onChange={(event) => {
                      setValue('createNewDocument', event.target.checked, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    size="small"
                  />
                }
                label={
                  T.translate('generic.removePagesCreateNewDocToggle') ||
                  'Create a new document'
                }
                style={{ marginBottom: createNewDocument ? '0.5rem' : 0 }}
              />
              {createNewDocument && (
                <div>
                  <TextForm
                    {...register('newDocName')}
                    label={
                      T.translate('generic.removePagesNewDocNameLabel') || 'New document name'
                    }
                    placeholder={
                      T.translate('generic.removePagesNewDocNamePlaceholder') ||
                      'Type a document name'
                    }
                    errors={errors?.newDocName}
                  />
                  {hasNewDocNameError && (
                    <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: '#dc3545' }}>
                      {T.translate('generic.removePagesNewDocNameRequired') ||
                        'Please enter a document name.'}
                    </div>
                  )}
                  <div style={{ marginTop: '0.6rem' }}>
                    <MoveDocument
                      control={control}
                      errors={errors}
                      canUnselect={false}
                      readOnly={isSubmitting}
                      currentFolderType={null}
                      menuPlacement="bottom"
                      defaultFolderName={autoGeneratedFolderName}
                      excludeTrialBundles={true}
                      label={
                        T.translate('generic.removePagesNewDocFolderLabel') || 'Destination folder'
                      }
                      folderError={
                        hasNewDocFolderError
                          ? T.translate('generic.removePagesNewDocFolderRequired') ||
                            'Please select a folder.'
                          : ''
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <Form>
            {fields.map((field, index) => {
              const parsedEntry = parsedEntries[index];
              const numericValue =
                parsedEntry?.normalized && /^\d+$/.test(parsedEntry.normalized)
                  ? parseInt(parsedEntry.normalized, 10)
                  : null;
              const errorMessage = parsedEntry?.error;
              return (
                <Row
                  key={field.id}
                  style={{
                    alignItems: 'flex-start',
                    marginBottom: '0.75rem',
                  }}
                >
                  <Col xs={9}>
                    <TextForm
                      {...register(`pagesToRemove.${index}.value`, {
                        validate: (value) => {
                          if (!value || !value.trim()) return true;
                          const parsed = parsePageToken(value);
                          return parsed.error || true;
                        },
                        onChange: (e) => {
                          const rawValue = e.target.value || '';
                          let sanitized = rawValue.replace(/[^0-9-]/g, '');
                          if (sanitized.startsWith('-')) {
                            sanitized = sanitized.slice(1);
                          }
                          const parts = sanitized.split('-');
                          if (parts.length > 2) {
                            sanitized = `${parts[0]}-${parts.slice(1).join('')}`;
                          }
                          e.target.value = sanitized;
                        },
                      })}
                      label={T.translate('generic.pageNumberOrRange')}
                      placeholder={T.translate('generic.pageNumberOrRangePlaceholder')}
                      errors={errors?.pagesToRemove?.[index]?.value}
                      onKeyDown={(e) => handlePageInputKeyDown(e, index, !!errorMessage)}
                    />
                    {errorMessage && (
                      <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: '#dc3545' }}>
                        {errorMessage}
                      </div>
                    )}
                  </Col>
                  <Col
                    xs={3}
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: '0.25rem',
                      paddingTop: '0.2rem',
                    }}
                  >
                    {numericValue && (
                      <Button
                        variant="text"
                        onClick={() => handleGoToPage(numericValue)}
                        sx={{
                          textTransform: 'none',
                          color: '#4567e5',
                          padding: '4px 8px',
                          fontSize: '0.8rem',
                          '&:hover': {
                            backgroundColor: 'rgba(69, 103, 229, 0.04)',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {T.translate('generic.goToPage', { pageNumber: numericValue }) ||
                          `Go to page ${numericValue}`}
                      </Button>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => remove(index)}
                      disabled={fields.length === 1}
                      sx={{ color: '#b23b3b' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Col>
                </Row>
              );
            })}
            <Row>
              <Col xs={12} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <IconButton
                  size="small"
                  onClick={() => append({ value: '' })}
                  sx={{
                    bgcolor: '#7BA0F4',
                    borderRadius: '5px',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#1565c0',
                    },
                  }}
                >
                  <span style={{ marginRight: '6px', fontSize: '0.8rem' }}>
                    {T.translate('generic.add').toLocaleUpperCase()}
                  </span>
                  <AddCard fontSize="small" />
                </IconButton>
              </Col>
            </Row>
          </Form>
          <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#4b4b4b' }}>
            {T.translate('generic.removePagesSelectionCount', { count: totalPagesSelected })}
          </div>
          {pagesToRemoveDisplay.length > 0 && (
            <div style={{ marginTop: '0.35rem', fontSize: '0.85rem', color: '#4b4b4b' }}>
              <strong>{T.translate('generic.removePagesNormalizedList')}:</strong>{' '}
              {pagesToRemoveDisplay.join(', ')}
            </div>
          )}
          {debugCheckboxStatus && (
            <div style={{ marginTop: '0.35rem', fontSize: '0.85rem', color: '#c0392b' }}>
              {debugCheckboxStatus}
            </div>
          )}
          {Number.isFinite(maxPages) && !leavesAtLeastOnePage && (
            <div style={{ marginTop: '0.35rem', fontSize: '0.85rem', color: '#dc3545' }}>
              {T.translate('generic.removePagesMustLeaveOne')}
            </div>
          )}
        </div>
        <div ref={viewerWrapperRef} data-remove-pages-viewer="true">
          {file?.id && (
            <FileViewer
              key={`remove-pages-viewer-${file.id}-${viewerInstanceKey}`}
              ref={fileViewerRef}
              fileId={file.id}
              isPreviewMode
              copyable
              onViewerReady={() => setViewerReady(true)}
            />
          )}
        </div>
      </Split>
    </Modal>
  );
};

ModalRemovePages.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  selectedRows: PropTypes.array.isRequired,
};

export default ModalRemovePages;
